import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Youtube, MapPin } from "lucide-react";
import { sendContact } from "../lib/api";

const EMAIL = "teacuppuppies4sale@gmail.com";
const YOUTUBE = "https://youtube.com/@tinyteacuppuppies";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await sendContact(form);
      toast.success("Thank you! Your message has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get In Touch"
        title={["Let's find you", "a little one."]}
        subtitle="Questions about a puppy, delivery, or an upcoming litter? Send us a note and we'll respond personally."
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="bg-[#2A2118] rounded-md p-10 h-full text-[#FDFBF7]">
              <h2 className="font-heading text-3xl font-light mb-8">Reach us directly</h2>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-start gap-4 mb-7 group">
                <Mail size={20} className="text-[#E6C5C0] mt-1" />
                <div>
                  <p className="label-eyebrow text-[#C8A98E] mb-1">Email</p>
                  <p className="text-[#FDFBF7] group-hover:text-[#E6C5C0] transition-colors">{EMAIL}</p>
                </div>
              </a>
              <a href={YOUTUBE} target="_blank" rel="noopener noreferrer" data-testid="contact-youtube-link" className="flex items-start gap-4 mb-7 group">
                <Youtube size={20} className="text-[#E6C5C0] mt-1" />
                <div>
                  <p className="label-eyebrow text-[#C8A98E] mb-1">Watch Us</p>
                  <p className="text-[#FDFBF7] group-hover:text-[#E6C5C0] transition-colors">@tinyteacuppuppies</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[#E6C5C0] mt-1" />
                <div>
                  <p className="label-eyebrow text-[#C8A98E] mb-1">Delivery</p>
                  <p className="text-[#d8c4b8] font-light">USA nationwide &amp; worldwide via flight nanny.</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-[#E2D1C3] rounded-md p-10">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="label-eyebrow text-[#8a7a68]">Name</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-2 border-[#E2D1C3] focus-visible:ring-[#C8A98E]" placeholder="Your name" />
                </div>
                <div>
                  <Label className="label-eyebrow text-[#8a7a68]">Email</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className="mt-2 border-[#E2D1C3] focus-visible:ring-[#C8A98E]" placeholder="you@email.com" />
                </div>
                <div>
                  <Label className="label-eyebrow text-[#8a7a68]">Phone (optional)</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="mt-2 border-[#E2D1C3] focus-visible:ring-[#C8A98E]" placeholder="+1 ..." />
                </div>
                <div>
                  <Label className="label-eyebrow text-[#8a7a68]">Subject</Label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="mt-2 border-[#E2D1C3] focus-visible:ring-[#C8A98E]" />
                </div>
              </div>
              <div className="mt-6">
                <Label className="label-eyebrow text-[#8a7a68]">Message</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} className="mt-2 border-[#E2D1C3] focus-visible:ring-[#C8A98E]" placeholder="Tell us which puppy or litter you're interested in..." />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="mt-8 w-full bg-[#2A2118] text-[#FDFBF7] py-4 rounded-sm text-sm tracking-wide hover:bg-[#3d3226] transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
